import type { DataGridColumn } from '../types';

/**
 * Read the export-friendly value for a column on a given row.
 * Priority : `exportValue` → `sortValue` → `accessor` → null.
 *
 * The accessor is expected to return the value in its NATURAL UNIT (see
 * `DataGridDataType` docs — EUR not centimes, percent as 0..1, duration
 * as minutes). The column rule system handles type-specific formatting
 * downstream (renderer + xlsx numFmt).
 */
export function readExportValue<T>(
  column: DataGridColumn<T>,
  row: T
): string | number | Date | null | undefined {
  if (column.exportValue) return column.exportValue(row);
  if (column.sortValue) return column.sortValue(row);
  const acc = column.accessor;
  if (acc === undefined) return null;
  if (typeof acc === 'function') return acc(row);
  const value = row[acc];
  if (
    value === null ||
    value === undefined ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    value instanceof Date
  ) {
    return value as string | number | Date | null | undefined;
  }
  return String(value);
}

/**
 * Filter out purely-visual columns (actions with no textual value, or
 * columns the caller explicitly flagged with `exportSkip: true`).
 * The returned list preserves the original order.
 */
export function resolveExportColumns<T>(
  columns: DataGridColumn<T>[]
): DataGridColumn<T>[] {
  return columns.filter((col) => {
    if (col.exportSkip) return false;
    return (
      col.exportValue !== undefined ||
      col.sortValue !== undefined ||
      col.accessor !== undefined ||
      col.exportFormula !== undefined
    );
  });
}

/**
 * Plain header string for a column — used as the column title in both CSV
 * and Excel. Prefers `exportHeader`, then the `header` if it's a string,
 * else the column id as a last-resort fallback.
 */
export function getExportHeader<T>(col: DataGridColumn<T>): string {
  if (col.exportHeader) return col.exportHeader;
  if (typeof col.header === 'string') return col.header;
  return col.id;
}
